/**
 * voice-v8 — das Verworfene eine Minute lang aufheben. (26.09.2026 abends,
 * Ticket 2674, Nachbesserung zu pre.3346)
 *
 * „Den Plan würde ich, ehrlich gesagt, verwerfen." warf das ganze Diktat
 * weg — das Komma galt als Pause. Die Regel ist jetzt strenger
 * (shared/sende-wort.ts), aber ein gesprochenes „Verwerfen" bleibt ein
 * Wort, das der Erkenner auch einmal falsch zuordnet. Darum ist es
 * umkehrbar: es liegt ZURUECKHOLEN_MS lang hier, nur im Speicher dieser
 * Seite — nie auf dem Server, nie im Gerätespeicher.
 *
 * 26.09.2026 spät (dritte Runde): Hier lag bis dahin nur der TEXT der
 * Live-Mitschrift. Stand sie (Ticket 2672) oder hinkte sie nach, fehlte
 * genau das, was zurückgeholt werden sollte. Jetzt liegt die AUFNAHME hier;
 * beim Zurückholen wird sie ganz erkannt, wie beim Senden. Der Text der
 * Mitschrift ist nur noch der Rückfall, wenn die Erkennung scheitert.
 *
 * ⚠️ Höchstens ABLAGE_HOECHSTENS Aufnahmen. Ein zweites Verwerfen ersetzt
 *    das erste NICHT, solange das erste noch zurückzuholen ist — beide
 *    bleiben. Erst ein drittes verdrängt das älteste, und das wird gesagt.
 *
 * 26.09.2026 (vierte Runde): Ob etwas hier liegt, entscheidet der TON, und
 * herausgenommen wird erst nach der gelungenen Erkennung (`nimm`, dann
 * `hole`) — scheitert sie, bleibt die Aufnahme, und es erscheint „Erneut
 * versuchen" wie beim Senden (lib/feld-auftraege.ts).
 */

/** So lange lässt sich ein verworfenes Diktat zurückholen. */
export const ZURUECKHOLEN_MS = 60_000;

/** So viele verworfene Aufnahmen liegen höchstens bereit. */
const ABLAGE_HOECHSTENS = 2;

/** Was die Seite sagt, wenn ein drittes Verwerfen das älteste verdrängt (vierte Runde). */
export const VERDRAENGT_HINWEIS = "Das älteste Verworfene ist nicht mehr zurückholbar — höchstens zwei liegen bereit.";

/**
 * So lange nach dem letzten Verwerfen ist ein Diktat aus nur „zurückholen"
 * oder „doch nicht" noch ein Befehl — auch wenn nichts mehr daliegt (dann:
 * „nichts mehr zum Zurückholen", statt als Nachricht an Vespera).
 *
 * ⚠️ 26.09.2026 (vierte Runde): zurück auf die Minute des Zurückholens.
 *    Die fünf Minuten der dritten Runde schluckten echte Antworten: wer
 *    kurz nach einem Verwerfen auf eine Frage „Doch nicht." sagte, bekam
 *    „nichts mehr zum Zurückholen", und die Antwort ging nie hinaus.
 */
export const ZURUECKHOLEN_BEFEHL_MS = ZURUECKHOLEN_MS;

/** Eine verworfene Aufnahme. */
export interface Verworfenes {
  /** Die ganze Aufnahme — hier liegt nur, was Ton hatte (siehe `lege`). */
  ton: Blob;
  mime: string;
  /** Was die Mitschrift ohne den Befehl zeigte — nur fürs Protokoll. */
  text: string;
  /** Stand „Verwerfen" für sich (Sprechpause davor)? Für die Erkennung. */
  fuerSich: boolean;
  seit: number;
  /**
   * 22.10.2026 (Prüfauflage, server/ptt-salat.ts): vom Server als Silbensalat verworfen, nicht von Martin. Nur per Tipp auf
   * seine Notiz zurückzuholen: er öffnet das gesprochene Befehlsfenster nie (sonst schluckte „Doch nicht.“ als echte Antwort
   * nach einem Salat — Ticket 2674), das gesprochene „zurückholen“ nimmt ihn nie, und er verdrängt nie einen echten Eintrag.
   */
  salat?: boolean;
  /**
   * Schon zurückgeholt? (vierte Runde) Gesetzt erst NACH der gelungenen
   * Erkennung (`hole`) — ein zweiter Auftrag für dieselbe Aufnahme (Tipp
   * und Wort, oder „Erneut versuchen") legt den Text dann nicht noch
   * einmal ins Feld.
   */
  zurueckgeholt?: boolean;
}

/** Was beim Weglegen geschah. */
interface Gelegt {
  neu: Verworfenes;
  /**
   * Was dafür nicht mehr zurückzuholen ist (ein drittes Verwerfen verdrängt
   * das älteste) — die Seite sagt es sichtbar (vierte Runde).
   */
  verdraengt: Verworfenes[];
}

interface VerworfenAblage {
  /**
   * Weglegen. `null`, wenn die Aufnahme keinen Ton hatte — darüber
   * entscheidet der TON (die Leitung trug Ton, der Rekorder lieferte eine
   * Aufnahme), nicht der Text der Mitschrift (vierte Runde: stand sie, lag
   * genau das Diktat, auf das es ankam, nie in der Ablage). Ein zu altes
   * oder schon zurückgeholtes wird ersetzt; zwei gültige bleiben, ein
   * drittes verdrängt das älteste.
   */
  lege(
    v: { ton: Blob | null; hatteTon: boolean; mime: string; text: string; fuerSich: boolean; salat?: boolean },
    jetzt?: number,
  ): Gelegt | null;
  /** Liegt noch etwas bereit? */
  da(jetzt?: number): boolean;
  /** Liegt genau dieses noch bereit? */
  enthaelt(v: Verworfenes, jetzt?: number): boolean;
  /**
   * NACHSEHEN, ohne herauszunehmen — ein bestimmtes (Tipp auf seine
   * Notiz) oder das jüngste (gesprochenes „zurückholen"). `null`, wenn
   * nichts (mehr) da ist. (Vierte Runde: nichts verlässt die Ablage vor
   * einer gelungenen Erkennung.)
   */
  nimm(v?: Verworfenes | null, jetzt?: number): Verworfenes | null;
  /**
   * Nach der gelungenen Erkennung: als zurückgeholt vermerken und aus der
   * Ablage nehmen. `false`, wenn es schon zurückgeholt war.
   */
  hole(v: Verworfenes): boolean;
  /** Ist ein Diktat aus nur „zurückholen" gerade ein Befehl? */
  befehlGilt(jetzt?: number): boolean;
}

export function neueVerworfenAblage(): VerworfenAblage {
  let eintraege: Verworfenes[] = [];
  let zuletztVerworfen: number | null = null;
  const gilt = (v: Verworfenes, jetzt: number): boolean => !v.zurueckgeholt && jetzt - v.seit < ZURUECKHOLEN_MS;
  const aufraeumen = (jetzt: number): void => {
    eintraege = eintraege.filter((v) => gilt(v, jetzt));
  };
  return {
    lege(v, jetzt = Date.now()) {
      if (!v.ton || !v.hatteTon) return null;
      aufraeumen(jetzt);
      const salat = v.salat === true;
      const verdraengt: Verworfenes[] = [];
      while (eintraege.length >= ABLAGE_HOECHSTENS) {
        // Zuerst geht der älteste Salat; ein Salat verdrängt NIE einen echten Eintrag — dann wird er nicht aufgehoben.
        const i = eintraege.findIndex((x) => x.salat);
        if (i < 0 && salat) return null;
        const [alt] = eintraege.splice(i < 0 ? 0 : i, 1);
        if (alt) verdraengt.push(alt);
      }
      const neu: Verworfenes = {
        ton: v.ton,
        mime: v.mime,
        text: v.text.trim(),
        fuerSich: v.fuerSich,
        seit: jetzt,
        ...(salat ? { salat: true } : {}),
      };
      eintraege.push(neu);
      // Nur Martins eigenes Verwerfen öffnet das gesprochene Befehlsfenster.
      if (!salat) zuletztVerworfen = jetzt;
      return { neu, verdraengt };
    },
    da(jetzt = Date.now()) {
      aufraeumen(jetzt);
      return eintraege.length > 0;
    },
    enthaelt(v, jetzt = Date.now()) {
      aufraeumen(jetzt);
      return eintraege.includes(v);
    },
    nimm(v, jetzt = Date.now()) {
      aufraeumen(jetzt);
      // Ohne bestimmtes (gesprochenes „zurückholen“): das jüngste ECHTE — ein Salat nur per Tipp auf seine Notiz.
      return (v ? eintraege.find((x) => x === v) : eintraege.filter((x) => !x.salat).at(-1)) ?? null;
    },
    hole(v) {
      if (v.zurueckgeholt) return false;
      v.zurueckgeholt = true;
      eintraege = eintraege.filter((x) => x !== v);
      return true;
    },
    befehlGilt(jetzt = Date.now()) {
      aufraeumen(jetzt);
      if (eintraege.some((x) => !x.salat)) return true;
      return zuletztVerworfen !== null && jetzt - zuletztVerworfen < ZURUECKHOLEN_BEFEHL_MS;
    },
  };
}
